import { SettingOutlined } from '@ant-design/icons';
import { LeadSourceNode, SalesConfig, StoreSalesInfoDoc } from '@gooduncles/gu-app-schema';
import { Button, DatePicker, Spin } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';

import { dayjsesToStringDateWhere, getDefaultDates, getDisableFutureDate } from 'src/utils/date-util';
import { observeSalesConfig, observeStoreSalesInfos, setSalesConfig } from 'src/utils/firebase/firebase-short-cut';

import SalesConfigDrawer from './SalesConfigDrawer';
import SalesManagementDashboard from './SalesManagementDashboard';
import SalesManagementTable from './SalesManagementTable';

const { RangePicker } = DatePicker;

// 기본값: 최근 90일
const defaultDates = getDefaultDates(90, 0);

const emptySalesConfig: SalesConfig = {
  leadSourceTree: [],
  setupStatuses: [],
};

/** LeadSourceNode 트리에서 depth 0 라벨 목록 추출 */
export const getLeadSourceOptions = (tree: LeadSourceNode[]) => tree.map((node) => node.label);

/** 특정 leadSource에 대한 depth 1 children 조회 */
export const getLeadSourceChildren = (tree: LeadSourceNode[], leadSource: string): LeadSourceNode[] | null => {
  const node = tree.find((n) => n.label === leadSource);
  return node?.children ?? null;
};

/** 특정 leadSource > leadSourceDetail에 대한 depth 2 children 조회 */
export const getLeadSourceAgentChildren = (
  tree: LeadSourceNode[],
  leadSource: string,
  leadSourceDetail: string,
): LeadSourceNode[] | null => {
  const children = getLeadSourceChildren(tree, leadSource);
  if (!children) return null;
  const node = children.find((n) => n.label === leadSourceDetail);
  return node?.children ?? null;
};

const SalesManagement: FC = () => {
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  const [salesConfig, setSalesConfigState] = useState<SalesConfig>(emptySalesConfig);

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>(defaultDates);
  const [rowData, setRowData] = useState<StoreSalesInfoDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [startDate, endDate] = useMemo(
    () => (dateRange[0] && dateRange[1] ? dayjsesToStringDateWhere(dateRange[0], dateRange[1]) : ['', '']),
    [dateRange],
  );

  useEffect(() => {
    const subscription = observeSalesConfig().subscribe({
      next: (doc) => {
        if (doc) {
          setSalesConfigState({
            leadSourceTree: doc.leadSourceTree ?? [],
            setupStatuses: doc.setupStatuses ?? [],
          });
        } else {
          // 문서가 없으면 초기 생성
          setSalesConfig(emptySalesConfig);
          setSalesConfigState(emptySalesConfig);
        }
      },
      error: (error) => {
        console.error('Failed to observe SalesConfig:', error);
      },
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;

    setIsLoading(true);

    const subscription = observeStoreSalesInfos(
      [
        ['storeCreatedAt', '>=', startDate],
        ['storeCreatedAt', '<=', endDate],
      ],
      {
        sortKey: 'storeCreatedAt',
        orderBy: 'desc',
      },
    ).subscribe({
      next: (docs) => {
        setRowData(docs);
        setIsLoading(false);
      },
      error: (error) => {
        console.error('Failed to fetch store sales info:', error);
        setIsLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [startDate, endDate]);

  return (
    <div className='height100 flexColumn' style={{ gap: 24 }}>
      <SalesManagementDashboard leadSourceTree={salesConfig.leadSourceTree} />

      <div style={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <div className='height100 flexRow flexCenter'>
            <Spin size='large' />
          </div>
        ) : (
          <SalesManagementTable
            rowData={rowData}
            salesConfig={salesConfig}
            toolbarLeftContent={
              <div className='flexRow flexAlignCenter' style={{ gap: 8 }}>
                <span>조회 기간:</span>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
                  disabledDate={getDisableFutureDate()}
                  allowClear={false}
                />
              </div>
            }
            toolbarRightContent={
              <Button icon={<SettingOutlined />} onClick={() => setConfigDrawerOpen(true)}>
                유입 트리 편집
              </Button>
            }
          />
        )}
      </div>
      <SalesConfigDrawer open={configDrawerOpen} onClose={() => setConfigDrawerOpen(false)} />
    </div>
  );
};

export default SalesManagement;
